import { create } from "zustand"
import { devtools } from "zustand/middleware"
import type {
  UpholsteryDataState,
  UpholsteryUIState,
  UpholsteryNavigationState,
  UpholsterySelectionsState,
  StepKey,
} from "../components/types/store-types"
import type { BodySelection } from "../components/types/component-types"
import type { FactoryMatch } from "@views/Upholstery/types"
import { filterFactoryMatchesForActiveConfig } from "@views/UpholsteryV2/helpers/factory/factory-match-filter"
import { RowType } from "../components/types/pricing-types"
import type { RowsSelection } from "../components/types/component-types"
import { resolveRowCountFromRowsConfig } from "@views/UpholsteryV2/constants/row-count-choice"

// Export options store
export { useUpholsteryOptionsStore } from "./options-store"
export { usePdmDataStore } from "./pdm-data-store"

/**
 * ===== DATA STORE =====
 * Holds vehicle data, active configurations, and derived context (e.g., rowCount/rowType).
 * Single source of truth for pricing-relevant selections; UI stores should not duplicate this.
 */
export const useUpholsteryDataStore = create<UpholsteryDataState>()(
  devtools(
    (set, get) => ({
      // Initial state
      cars: [],
      selectedVehicle: null,
      activeConfigurations: {},
      rowCount: 0,
      rowType: "2-row" as RowType,
      m3Number: "",
      factoryMatchSku: "",
      customBuildSku: "",

      // Actions
      actions: {
        setCars: (cars) => set({ cars }),
        setSelectedVehicle: (vehicle) => set({ selectedVehicle: vehicle }),
        setM3Number: (value) =>
          set((state) =>
            state.m3Number === value ? state : { m3Number: value }
          ),
        setSkus: (payload) =>
          set((state) => {
            if (
              state.factoryMatchSku === payload.factoryMatchSku &&
              state.customBuildSku === payload.customBuildSku
            ) {
              return state
            }
            return {
              factoryMatchSku: payload.factoryMatchSku,
              customBuildSku: payload.customBuildSku,
            }
          }),
        setActiveConfigurations: (configurations) =>
          set((state) => {
            const nextConfigs =
              typeof configurations === "function"
                ? configurations(state.activeConfigurations)
                : configurations

            // Skip notify when updater returned the same object (no-op paths).
            if (nextConfigs === state.activeConfigurations) {
              return state
            }

            const rowCount = resolveRowCountFromRowsConfig(nextConfigs)
            const rowType: RowType = rowCount === 3 ? "3-row" : "2-row"

            return {
              activeConfigurations: nextConfigs,
              rowCount,
              rowType,
            }
          }),
        /**
         * Extract category optionValues by step key from the selected vehicle.
         * Use this to read the option lists for any allowed step key
         * (body, rows, build, kit, pattern, design, material, factory-match).
         */
        getStepOptionValues: (stepKey: StepKey) => {
          const vehicle = get().selectedVehicle
          const step = vehicle?.steps?.find((s) => s.key === stepKey)
          return step?.category?.optionValues ?? []
        },
        /**
         * Factory match card filtering by body/rows slug.
         * - UpholsteryV2: `factoryMatchFilter` on step (PDM merge) — no Dato.
         * - Legacy upholstery: Dato `vehicle.options.factoryMatch` (unchanged).
         */
        getFilteredFactoryMatches: (factoryMatch: FactoryMatch[]) => {
          const state = get()
          const { selectedVehicle, activeConfigurations } = state

          if (factoryMatch.length <= 1) {
            return factoryMatch
          }

          return filterFactoryMatchesForActiveConfig(
            factoryMatch,
            activeConfigurations,
            selectedVehicle?.steps,
            (selectedVehicle as { options?: unknown } | null)?.options
          )
        },
      },
    }),
    { name: "UpholsteryDataStore" }
  )
)

/**
 * ===== UI STORE =====
 * UI-only flags (modal visibility, factory-build toggle, etc.).
 * Keep transient presentation state separate from pricing/config data.
 */
export const useUpholsteryUIStore = create<UpholsteryUIState>()(
  devtools(
    (set) => ({
      // Initial state
      isFactoryBuild: false,
      showForm: false,
      showYourPicksModal: false,
      showPrices: false,
      showPriceCostCode: false,
      showM3Code: true,
      fullLeatherKitCallForPricing: false,

      // Actions
      actions: {
        setIsFactoryBuild: (value) => set({ isFactoryBuild: value }),
        setShowForm: (show) => set({ showForm: show }),
        setShowYourPicksModal: (show) => set({ showYourPicksModal: show }),
        setShowPrices: (show) => set({ showPrices: show }),
        setShowPriceCostCode: (show) => set({ showPriceCostCode: show }),
        setShowM3Code: (show) => set({ showM3Code: show }),
        setFullLeatherKitCallForPricing: (enabled) =>
          set({ fullLeatherKitCallForPricing: enabled }),
      },
    }),
    { name: "UpholsteryUIStore" }
  )
)

/**
 * ===== NAVIGATION STORE =====
 * Tracks wizard navigation state (current step/substep, summary mode).
 * Keeps navigation concerns isolated from selection and UI stores.
 */
export const useUpholsteryNavigationStore = create<UpholsteryNavigationState>()(
  devtools(
    (set) => ({
      // Initial state
      currentOption: null,
      currentSubOption: null,
      currentStepIndex: 0,
      active: null,
      isSummaryActive: false,
      rowCountChoiceConfirmed: false,

      // Actions
      actions: {
        setCurrentOption: (option) => set({ currentOption: option }),
        setCurrentSubOption: (subOption) =>
          set({ currentSubOption: subOption }),
        setCurrentStepIndex: (index) => set({ currentStepIndex: index }),
        setActive: (active) => set({ active }),
        setIsSummaryActive: (active) => set({ isSummaryActive: active }),
        setRowCountChoiceConfirmed: (confirmed) =>
          set({ rowCountChoiceConfirmed: confirmed }),

        // Compound actions for atomic updates
        navigateToStep: (stepIndex, stepKey, subStepKey = null) =>
          set({
            currentStepIndex: stepIndex,
            currentOption: stepKey,
            active: stepKey,
            currentSubOption: subStepKey,
            isSummaryActive: false,
          }),

        navigateToSummary: () =>
          set({
            currentOption: "summary",
            active: "summary",
            isSummaryActive: true,
          }),

        resetNavigation: () =>
          set({
            currentOption: null,
            currentSubOption: null,
            currentStepIndex: 0,
            active: null,
            isSummaryActive: false,
            rowCountChoiceConfirmed: false,
          }),
      },
    }),
    { name: "UpholsteryNavigationStore" }
  )
)

/**
 * ===== SELECTIONS STORE =====
 * Holds user selections for build/kit/materials, separate from configs/UI.
 * Used for features like bulk resets without touching the data store.
 */
export const useUpholsterySelectionsStore = create<UpholsterySelectionsState>()(
  devtools(
    (set, get) => ({
      // Initial state
      selectedBuild: "custom",
      selectKitType: "standard",
      materialSelections: [],
      rowsSelections: [],
      bodySelections: [],

      factoryMatchSelections: [],

      // Actions
      actions: {
        // ────────────────────────────────────────────────────────────────
        // BUILD & KIT
        // ────────────────────────────────────────────────────────────────
        setSelectedBuild: (value) => set({ selectedBuild: value }),
        setSelectKitType: (value) => set({ selectKitType: value }),

        // ────────────────────────────────────────────────────────────────
        // MATERIAL SELECTIONS
        // ────────────────────────────────────────────────────────────────
        // Maintains material selections (primary-color, insert, stitch) across substep navigation
        setMaterialSelections: (selections) =>
          set({ materialSelections: selections }),
        resetMaterialSelections: () => set({ materialSelections: [] }),

        // ────────────────────────────────────────────────────────────────
        // ROWS SELECTIONS
        // ────────────────────────────────────────────────────────────────
        setRowsSelections: (selections: RowsSelection[]) =>
          set({ rowsSelections: selections }),

        /**
         * Initialize rows selections from activeConfigurations.
         * Only initializes if store is empty (guards against overwriting existing selections).
         * Converts object format { [sectionId]: { value, sku } } to array format.
         */
        initializeRowsSelections: (
          initialSelections: Record<
            string,
            {
              value: string
              sku?: string
              slug?: string
              image?: string
              label?: string
            }
          >
        ) => {
          const state = get()
          if (state.rowsSelections.length > 0) return
          if (Object.keys(initialSelections).length === 0) return

          const rowsSelections: RowsSelection[] = Object.entries(
            initialSelections
          ).map(([key, data]) => ({
            sectionId: key,
            selectedValue: data.value,
            value: data.value,
            sku: data.sku ?? "",
            slug: data.slug ?? "",
            image: data.image ?? "",
            label: data.label ?? "",
          }))
          set({ rowsSelections })
        },

        resetRowsSelections: () => set({ rowsSelections: [] }),

        // ────────────────────────────────────────────────────────────────
        // BODY SELECTIONS
        // ────────────────────────────────────────────────────────────────
        setBodySelections: (selections: BodySelection[]) =>
          set({ bodySelections: selections }),

        /**
         * Initialize body selections from activeConfigurations.
         * Only initializes if store is empty (guards against overwriting existing selections).
         * Converts object format { [sectionId]: { value, sku } } to array format.
         */
        initializeBodySelections: (initialSelections) => {
          const state = get()
          if (state.bodySelections.length > 0) return
          if (Object.keys(initialSelections).length === 0) return

          const bodySelections: BodySelection[] = Object.entries(
            initialSelections
          ).map(([key, data]) => ({
            sectionId: key,
            selectedValue: data.value,
            sku: data.sku ?? "",
          }))
          set({ bodySelections })
        },

        resetBodySelections: () => set({ bodySelections: [] }),

        // ────────────────────────────────────────────────────────────────
        // FACTORY MATCH SELECTIONS
        // ────────────────────────────────────────────────────────────────
        // initializeFactoryMatchSelections: (initialSelections) => {
        //   const state = get()
        //   if (state.factoryMatchSelections.length > 0) return
        //   if (Object.keys(initialSelections).length === 0) return

        //   const factoryMatchSelections: FactoryMatchSelection[] = Object.entries(
        //     initialSelections
        //   ).map(([key, data]) => ({
        //     ...data,
        //     sectionId: key,
        //     selectedValue: data.value,
        //     sku: data.sku ?? "",
        //   }))
        //   set({ factoryMatchSelections })
        // },

        // ────────────────────────────────────────────────────────────────
        // UTILITY ACTIONS
        // ────────────────────────────────────────────────────────────────
        /**
         * Find selection by SKU across body and rows selections.
         * Searches bodySelections first, then rowsSelections.
         */
        // TODO: remove later if not needed
        findSelectionBySku: (sku: string) => {
          const state = get()
          const bodySelection = state.bodySelections.find(
            (sel) => sel.sku === sku
          )
          if (bodySelection) return bodySelection

          const rowsSelection = state.rowsSelections.find(
            (sel) => sel.sku === sku
          )
          return rowsSelection || null
        },

        // ────────────────────────────────────────────────────────────────
        // RESET ACTIONS
        // ────────────────────────────────────────────────────────────────
        resetSelections: () =>
          set({
            selectedBuild: "custom",
            selectKitType: "standard",
            materialSelections: [],
            rowsSelections: [],
            bodySelections: [],
          }),
      },
    }),
    { name: "UpholsterySelectionsStore" }
  )
)
